<template>
  <Dialog
    :visible="isVisible"
    :pt="{
      root: { style: { background: 'white', borderRadius: '2rem', width: '500px' } },
      mask: {
        style: 'backdrop-filter: blur(1px); background-color: #00000014; pointer-events: auto; overflow-y: scroll;',
      },
    }"
    @hide="resetModal"
  >
    <template #container>
      <Button
        :class="$style.closeButton"
        icon="pi pi-times"
        class="p-button-rounded p-button-text"
        @click="resetModal"
      />

      <div :class="$style.wrapper">
        <div :class="$style.title">CSV Upload</div>
        <div v-if="loading">
          <div :class="$style.progressTitle">
            <VdIcon store="fas" name="fa-spinner" size="medium" color="orange" class="fa-spin" />
            Upload in Progress
          </div>
        </div>
        <div v-else>
          <div v-if="success">
            <div :class="$style.sucessTitle">CSV file successfully imported</div>
            <div>
              <VdIcon store="fas" name="fa-check" size="medium" color="green" />
              {{ count }} {{ entityLabel }}
            </div>
          </div>
          <div v-else>
            <div :class="$style.errorTitle">Encountered {{ errors.length }} errors</div>
            <ul>
              <li v-for="(bodies, title) in formatErrors(errors as string[])" :key="title">
                <b>{{ title }}</b>
                <ul :class="$style.errorlist">
                  <li v-for="(body, index) in bodies" :key="index">
                    <VdIcon store="fas" name="fa-times" size="medium" color="red" />
                    {{ body }}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
        <div class="p-fluid">
          <div :class="$style.buttonContainer">
            <Button
              :class="$style.actionButton"
              label="Close"
              :disabled="loading"
              :loading="loading"
              @click="resetModal"
            />
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from "primevue/dialog";
import Button from "primevue/button";

defineProps({
  isVisible: Boolean,
  loading: Boolean,
  count: Number,
  success: Boolean,
  entityLabel: {
    type: String,
    default: "Offers",
  },
  errors: {
    type: Array,
    default: () => [],
    validator: (value: string[]) => value.every((item) => typeof item === "string"),
  },
});

const emit = defineEmits(["close"]);

const resetModal = () => {
  emit("close");
};

const groupErrors = (formattedErrors: { title: string; body: string }[]) =>
  formattedErrors.reduce((groupedErrors: { [key: string]: string[] }, formatedError) => {
    const { title, body } = formatedError;

    if (!groupedErrors[title]) {
      groupedErrors[title] = [];
    }
    groupedErrors[title].push(body);

    return groupedErrors;
  }, {});

const formatErrors = (rawErrors: string[]) => {
  const lineRegex = /^Line \d+:/;

  const formattedErrors = rawErrors.map((error) => {
    const match = error.match(lineRegex);
    if (!match) {
      return { title: "CSV Errors:", body: error };
    }
    return { title: match[0], body: error.replace(lineRegex, "") };
  });

  return groupErrors(formattedErrors);
};
</script>
<style module>
.wrapper {
  background-color: white;
  padding: 2rem;
  border-radius: 2rem;
}

.title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 2rem;
}

.sucessTitle {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 1rem;
  color: green;
}

.errorTitle {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 1rem;
  color: red;
}
.progressTitle {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 1rem;
  color: orange;
}

.buttonContainer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.actionButton {
  width: 100px;
}

.closeButton {
  position: absolute;
  right: 1rem;
  top: 1rem;
}

.errorlist {
  list-style-type: none;
  padding-left: 0;
  padding-top: 10px;
  padding-bottom: 20px;
}

.errorlist li {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0.5rem;
}
</style>
